import { QuestionOption } from '../models/question-option.model';
import { CarouselType, ChoiceQuestionTypes, MultiChoiceQuestionTypes } from '../models/question-type.enum';
import { Question } from '../models/question.model';
import { SurveyAutoPopulateCandidate } from '../models/survey-auto-populate-candidate.interface';

export class SurveyAutomaticAnswers {
  getCandidates(pageQuestions: Question[], answers: Record<string, string>): SurveyAutoPopulateCandidate[] {
    return pageQuestions
      .map((question) => this.buildCandidate(question, answers[question.id] as string | undefined))
      .filter((candidate): candidate is SurveyAutoPopulateCandidate => candidate != null);
  }

  private parse<T>(raw: string): T | null {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private buildCandidate(question: Question, rawAnswer: string | undefined): SurveyAutoPopulateCandidate | null {
    if (!question.optionsSettings?.autopopulateInd || !question.requiredInd || !ChoiceQuestionTypes.includes(question.type)) {
      return null;
    }

    const questionOptions = this.getVisibleOptions(question);
    if (questionOptions.length !== 1) {
      return null;
    }

    const canAutoPopulate = CarouselType.includes(question.type) ? this.isCarouselAnswerEmpty(rawAnswer) : this.isChoiceAnswerEmpty(rawAnswer);
    if (!canAutoPopulate) {
      return null;
    }

    const selectedOption = questionOptions[0];

    return {
      question,
      selectedOption,
      answerJson: this.mapAnswer(question, selectedOption)
    };
  }

  private getVisibleOptions(question: Question): QuestionOption[] {
    return (question.options ?? []).filter((option) => option.visible !== false);
  }

  private isChoiceAnswerEmpty(rawAnswer?: string): boolean {
    if (!rawAnswer) {
      return true;
    }

    const parsedAnswer = this.parse<{ value?: string | string[] }>(rawAnswer);
    return this.isSelectedValueEmpty(parsedAnswer?.value);
  }

  private isCarouselAnswerEmpty(rawAnswer?: string): boolean {
    if (!rawAnswer) {
      return true;
    }

    const parsedAnswer = this.parse<Record<string, { value?: string | string[] }>>(rawAnswer);
    if (!parsedAnswer) {
      return true;
    }

    return Object.values(parsedAnswer).every((cardAnswer) => this.isSelectedValueEmpty(cardAnswer?.value));
  }

  private isSelectedValueEmpty(value?: string | string[] | null): boolean {
    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return value == null || value === '';
  }

  private mapAnswer(question: Question, selectedOption: QuestionOption): string {
    const selectedValue = MultiChoiceQuestionTypes.includes(question.type) ? [selectedOption.value] : selectedOption.value;

    if (CarouselType.includes(question.type)) {
      return JSON.stringify(
        (question.cards ?? []).reduce(
          (result, card) => {
            result[card.id] = {
              value: Array.isArray(selectedValue) ? [...selectedValue] : selectedValue,
              otherText: ''
            };
            return result;
          },
          {} as Record<string, { value: string | string[]; otherText: string }>
        )
      );
    }

    return JSON.stringify({
      value: selectedValue,
      otherText: ''
    });
  }
}
